import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, MessageCircle, Linkedin, Home as HomeIcon, Shield, Star, ArrowRight, ExternalLink } from "lucide-react";
import EclipseLogo from "@/components/EclipseLogo";
import { BUSINESS_INFO, MAPS_URL } from "@/lib/business-info";

// Site-specific contact info. Eclipse home uses 2 separate numbers (WA + phone fijo);
// FlexShade & Skycover (velarias) keep the unified single number.
const SITE_CONTACTS = {
    eclipse: { tel: "+52 81 8989 9822", telRaw: "528189899822", waRaw: "528116289267", email: "info@eclipsecubiertas.com" },
    flexshade: { tel: "+52 81 8989 9822", telRaw: "528189899822", waRaw: "528116289267", email: "info@eclipsecubiertas.com" },
    velarias: { tel: "+52 81 8989 9822", telRaw: "528189899822", waRaw: "528116289267", email: "info@eclipsecubiertas.com" },
};

export default function Footer({ brandLine, currentSite = "eclipse" }) {
    const c = SITE_CONTACTS[currentSite] || SITE_CONTACTS.eclipse;
    return (
        <footer id="footer" data-testid="footer" className="bg-eclipse-primary text-white">
            <div className="container-eclipse py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
                    {/* COL 1 - logo + datos */}
                    <div data-testid="footer-col-info">
                        <div className="mb-6">
                            <EclipseLogo size="h-12" framed={false} alt="Eclipse Cubiertas SA de CV" />
                        </div>
                        {brandLine && (
                            <p className="text-xs text-neutral-400 mb-5 leading-relaxed italic">{brandLine}</p>
                        )}
                        <ul className="space-y-3 text-sm text-neutral-300">
                            <li className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-eclipse-cta" />
                                <span className="leading-relaxed">
                                    {BUSINESS_INFO.address.street}<br />
                                    {BUSINESS_INFO.address.detail}<br />
                                    {BUSINESS_INFO.address.neighborhood} · C.P. {BUSINESS_INFO.address.postalCode}<br />
                                    {BUSINESS_INFO.address.city}, {BUSINESS_INFO.address.state}
                                </span>
                            </li>
                            <li>
                                <a
                                    href={MAPS_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="footer-maps-btn"
                                    className="inline-flex items-center gap-2 mt-1 ml-7 px-3 py-2 rounded-sm border border-eclipse-cta/60 text-eclipse-cta hover:bg-eclipse-cta hover:text-white text-xs font-display font-bold tracking-wider uppercase transition-colors"
                                >
                                    <MapPin className="w-3.5 h-3.5" />
                                    Cómo llegar
                                    <ExternalLink className="w-3 h-3 opacity-80" />
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 text-eclipse-cta" />
                                <a href={`tel:+${c.telRaw}`} data-testid="footer-phone" className="hover:text-eclipse-cta transition-colors">{c.tel}</a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 text-eclipse-cta" />
                                <a href={`mailto:${c.email}`} data-testid="footer-email" className="hover:text-eclipse-cta transition-colors break-all">{c.email}</a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-eclipse-cta" />
                                <span>{BUSINESS_INFO.hours}</span>
                            </li>
                        </ul>
                    </div>

                    {/* COL 2 - Nuestros sitios (cross-site) */}
                    <div data-testid="footer-col-sites">
                        <h3 className="font-display font-bold text-sm tracking-wider mb-5 uppercase">Nuestros sitios</h3>
                        <ul className="space-y-4 text-sm">
                            <SiteLink to="/" Icon={HomeIcon} title="Eclipse Cubiertas" desc="Sitio corporativo madre" active={currentSite === "eclipse"} testId="footer-site-eclipse" />
                            <SiteLink to="/flexshade" Icon={Shield} title="FlexShade" desc="Toldos modulares de malla sombra" active={currentSite === "flexshade"} testId="footer-site-flexshade" />
                            <SiteLink to="/velarias" Icon={Star} title="Skycover" desc="Tensoestructuras premium a la medida" active={currentSite === "velarias"} testId="footer-site-velarias" />
                        </ul>
                    </div>

                    {/* COL 3 - nav */}
                    <div data-testid="footer-col-nav">
                        <h3 className="font-display font-bold text-sm tracking-wider mb-5 uppercase">Navegación</h3>
                        <ul className="space-y-3 text-sm text-neutral-300">
                            <li><Link to="/" className="hover:text-eclipse-cta transition-colors" data-testid="footer-link-inicio">Inicio</Link></li>
                            <li><Link to="/flexshade" className="hover:text-eclipse-cta transition-colors" data-testid="footer-link-flexshade">FlexShade</Link></li>
                            <li><Link to="/velarias" className="hover:text-eclipse-cta transition-colors" data-testid="footer-link-skycover">Skycover</Link></li>
                            <li><a href="/#proyectos" className="hover:text-eclipse-cta transition-colors" data-testid="footer-link-proyectos">Proyectos</a></li>
                            <li><a href="#footer" className="hover:text-eclipse-cta transition-colors" data-testid="footer-link-contacto">Contacto</a></li>
                        </ul>
                    </div>

                    {/* COL 4 - social */}
                    <div data-testid="footer-col-social">
                        <h3 className="font-display font-bold text-sm tracking-wider mb-5 uppercase">Síguenos</h3>
                        <div className="flex items-center gap-3 mb-6">
                            <SocialIcon href="#" label="Instagram" testId="footer-social-instagram"><Instagram className="w-5 h-5" /></SocialIcon>
                            <SocialIcon href="#" label="Facebook" testId="footer-social-facebook"><Facebook className="w-5 h-5" /></SocialIcon>
                            <SocialIcon href={`https://wa.me/${c.waRaw}`} label="WhatsApp" testId="footer-social-whatsapp"><MessageCircle className="w-5 h-5" /></SocialIcon>
                            <SocialIcon href="#" label="LinkedIn" testId="footer-social-linkedin"><Linkedin className="w-5 h-5" /></SocialIcon>
                        </div>
                        <p className="text-sm text-neutral-400 leading-relaxed">Especializados en arquitectura textil. Diseño, fabricación e instalación con equipo propio.</p>
                    </div>
                </div>
            </div>

            <div className="border-t border-neutral-800">
                <div className="container-eclipse py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-neutral-500">
                    <p>© 2026 Eclipse Cubiertas SA de CV · Todos los derechos reservados</p>
                    <div className="flex items-center gap-4">
                        <a href="#" className="hover:text-eclipse-cta transition-colors" data-testid="footer-privacy">Aviso de privacidad</a>
                        <span>·</span>
                        <a href="#" className="hover:text-eclipse-cta transition-colors" data-testid="footer-terms">Términos de uso</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function SiteLink({ to, Icon, title, desc, active, testId }) {
    return (
        <li>
            <Link to={to} data-testid={testId} className={`group flex items-start gap-3 transition-colors ${active ? "text-eclipse-cta" : "text-neutral-300 hover:text-eclipse-cta"}`}>
                <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={2} />
                <span>
                    <span className="font-display font-bold text-sm leading-tight flex items-center gap-1.5">
                        {title}
                        {active && <span className="text-[9px] font-bold bg-eclipse-cta text-white px-1.5 py-0.5 rounded uppercase tracking-wider">Aquí</span>}
                        {!active && <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </span>
                    <span className="block text-xs text-neutral-500 mt-0.5">{desc}</span>
                </span>
            </Link>
        </li>
    );
}

function SocialIcon({ href, label, children, testId }) {
    return (
        <a href={href} aria-label={label} target="_blank" rel="noopener noreferrer" data-testid={testId} className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-neutral-700 text-neutral-300 hover:bg-eclipse-cta hover:border-eclipse-cta hover:text-white transition-all">
            {children}
        </a>
    );
}
